import React from "react";
import { useParams } from "react-router-dom";

import schools from "./assets/data/schools";

import MetaInfo from "./components/MetaInfo";
import Header from "./components/Header";
import Footer from "./components/Footer";
import Banner from "./course-components/Banner";
import Overview from "./course-components/Overview";
import Subjects from "./course-components/Subjects";
import WhyChoose from "./course-components/WhyChoose";
import Highlights from "./course-components/Highlights";
import Eligibility from "./course-components/Eligibility";
import Structure from "./course-components/Structure";

import campusHero from "./course-details-assets/campus-hero.jpeg";
import classroomImage from "./course-details-assets/classroom.png";
import laboratoryImage from "./course-details-assets/laboratory.png";
import libraryImage from "./course-details-assets/library.png";
import seminarImage from "./course-details-assets/seminar-hall.png";
import studentLifeImage from "./course-details-assets/student-life.png";

import Specialisations from "./course-components/Specialisations";
import Skills from "./course-components/Skills";
import Outcomes from "./course-components/Outcomes";
import Careers from "./course-components/Careers";
import Placement from "./course-components/Placement";
import HigherStudies from "./course-components/HigherStudies";
import AdmissionProcess from "./course-components/AdmissionProcess";
import Faqs from "./course-components/Faqs";
import RelatedPrograms from "./course-components/RelatedPrograms";

import { Label, Title } from "./components/CommonComponents";
import Fees from "./course-components/Fees";

const images = {
  hero: campusHero,
  classroom: classroomImage,
  laboratory: laboratoryImage,
  library: libraryImage,
  seminar: seminarImage,
  studentLife: studentLifeImage,
};

const chapters = [
  ["overview", "Overview"], ["why", "Why ISBM"], ["highlights", "Highlights"],
  ["eligibility", "Eligibility"], ["structure", "Structure"], ["syllabus", "Syllabus"],
  ["specialisations", "Specialisations"], ["skills", "Skills"], ["outcomes", "Outcomes"],
  ["careers", "Careers"], ["placements", "Placements"], ["higher-studies", "Higher Studies"],
  ["fees", "Fees"], ["admissions", "Admissions"], ["faqs", "FAQs"], ["related", "Related Courses"],
];


const CourseDetails = () => {
  const { courseName } = useParams();

  const currentSchool = schools.find((school) =>
    school.courses?.some((course) => course.url === courseName),
  );

  const course = currentSchool?.courses?.find(
    (course) => course.url === courseName,
  );

  const relatedPrograms =
    currentSchool?.courses?.filter(
      (program) => program.url !== courseName
    ) ?? [];

  const title = course.short_title ? course.short_title : course.title;

  const availableChapters = chapters.filter(([id]) => {
    switch (id) {
      case "overview":
        return !!course?.description;

      case "why":
        return course?.reasons?.length > 0;

      case "highlights":
        return course?.highlights?.length > 0;

      case "eligibility":
        return course?.eligibility?.length > 0 || course?.eligibilityCriteria?.length > 0;

      case "structure":
        return course?.programStructure?.length > 0;

      case "syllabus":
        return course?.curriculum?.length > 0;

      case "specialisations":
        return course?.specializations?.length > 0;

      case "skills":
        return course?.skills?.length > 0;

      case "outcomes":
        return course?.outcomes?.length > 0;

      case "careers":
        return course?.careers?.length > 0;

      case "placements":
        return true; // Placement is always displayed

      case "higher-studies":
        return course?.higherStudies?.length > 0;

      case "fees":
        return true; // Fees is always displayed

      case "admissions":
        return true; // Admission is always displayed

      case "faqs":
        return course?.faqs?.length > 0;

      case "related":
        return relatedPrograms?.length > 0;

      default:
        return false;
    }
  });
  return (
    <>
      <MetaInfo schoolDetails="common" />
      <Header school={currentSchool} theme="gold" />

      {/* 01 — Banner | #071A2F */}
      <Banner course={course} images={images} school={currentSchool} />

      <main className="bg-white text-[#132337]">
        <div className="lg:grid lg:grid-cols-[230px_1fr]">

          {/* 00 — Sidebar Sticky | #071A2F */}
          <aside className="sticky top-0 z-30 hidden h-screen overflow-y-auto bg-[#071A2F] px-5 py-8 text-white lg:block">
            <Title light>Navigate</Title>
            <nav className="mt-7 space-y-1">
              {availableChapters.map(([id, label], i) =>
                <a href={`#${id}`} key={id} className="group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm text-white/55 transition hover:bg-white/10 hover:text-white">
                  <span className="text-[#D5AD57]">{String(i + 1).padStart(2, "0")}</span>{label}
                </a>
              )}
            </nav>
          </aside>

          <div className="">
            {/* 02 — Overview | White */}
            <Overview description={course.description} title={title} images={images} />
            {/* 03 — Why choose | Ivory */}
            {course.reasons?.length > 0 && (
              <WhyChoose reasons={course.reasons} title={title} images={images} />
            )}
            {/* 04 — Highlights | Navy */}
            {course.highlights?.length > 0 && (
              <Highlights highlights={course.highlights} images={images} title={title} />
            )}
            {/* 05 — Eligibility | White */}
            {course.eligibility?.length > 0 && (
              <Eligibility eligibility={course.eligibilityCriteria ? course.eligibilityCriteria : course.eligibility} title={title} images={images} />
            )}
            {/*06 — Structure | Light blue */}
            {course.programStructure?.length > 0 && (
              <Structure programStructure={course.programStructure} title={title} />
            )}
            {/*07 — Subjects | Ivory */}
            {course.curriculum?.length > 0 && (
              <Subjects curriculum={course.curriculum} title={title} />
            )}
            {/*08 — Specialisations | Light burgundy */}
            {course.specializations?.length > 0 && (
              <Specialisations specializations={course.specializations} title={title} specialization_selection={course.specialization_selection} />
            )}
            {/*09 — Skills | Soft blush */}
            {course.skills?.length > 0 && (
              <Skills skills={course.skills} title={title} />
            )}
            {/*10 — Outcomes | Burgundy */}
            {course.outcomes?.length > 0 && (
              <Outcomes outcomes={course.outcomes} title={title} images={images} />
            )}
            {/*11 — Careers | White */}
            {course.careers?.length > 0 && (
              <Careers careers={course.careers} title={title} />
            )}
            {/*12 — Placement | Navy */}
            <Placement />

            {/*13 — Higher studies | Light blue */}
            {course.higherStudies?.length > 0 && (
              <HigherStudies higherStudies={course.higherStudies} title={title} />
            )}
            {/*14 — Fees | Ivory */}
            <Fees title={title} />

            {/*15 — Admission Process | White */}
            <AdmissionProcess title={title} />

            {/*16 — FAQs | Light burgundy */}
            {course.faqs?.length > 0 && (
              <Faqs faqs={course.faqs} title={title} />
            )}
            {/*17 — Related | Navy */}
            {relatedPrograms?.length > 0 && (
              <RelatedPrograms relatedPrograms={relatedPrograms} />
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default CourseDetails;
